import React, { useCallback, useState } from 'react';
import { GetApp } from '@mui/icons-material';
import { DocumentNode } from 'graphql';
import { useLazyQuery } from '@apollo/react-hooks';
import { asyncOpenUrl } from 'utils/asyncOpenUrl';

import { TooltipButton, TooltipButtonProps } from '../buttons/TooltipButton';
import { ExportDialog } from './ExportDialog';
import { ErrorSnackbar, useErrorSnackbar } from '../notifications/ErrorSnackbar';
import { ColumnProps } from '../datagrid/column/Column';
import { useProfile } from '../../api/user/profile';
import { Permissions } from '../../api/permissions/permissions';
import { ExportField } from '../../api/types/globalTypes';

export type ColumnExportProps = Pick<ColumnProps, 'title' | 'field' | 'export' | 'hiddenByDefault'>;

export interface ExportButtonProps<Data extends any = any> extends Omit<TooltipButtonProps, 'onClick'> {
  tableId: string,
  tableWhere: any;
  exportFields: ColumnExportProps[],
  exportQuery: DocumentNode;
  exportLink: (data: Data) => string;
  children: React.ReactNode;
}

export function ExportButton<Data extends any>({
  tableId,
  tableWhere,
  exportFields,
  exportQuery,
  exportLink,
  children,
  ...tooltipButton
}: ExportButtonProps<Data>) {
  const profile = useProfile();
  const [openDialog, setOpenDialog] = useState(false);

  const exportAllowed = profile.userPermissions.some(
    (up) => up.permission_id === Permissions.EXPORT_TABLES,
  );

  // export table code
  const [exportTableData, {
    loading: exportLoading,
    error: exportError,
  }] = useLazyQuery<Data>(
    exportQuery,
    { fetchPolicy: 'no-cache' },
  );
  const exportErrorSnackbar = useErrorSnackbar(exportError);

  const exportHandler = useCallback(
    async (fields: ExportField[]) => {
      await asyncOpenUrl(async () => {
        const resp = await exportTableData({
          variables: { where: tableWhere, fields },
        });
        if (!resp?.data) {
          return null;
        }
        return exportLink(resp?.data);
      }, '_self');
      setOpenDialog(false);
    },
    [tableWhere, exportTableData],
  );

  if (!exportAllowed) {
    return null;
  }

  return (
    <>
      <TooltipButton
        title={(
          !exportAllowed
            ? (
              <>
                You dont have
                {' '}
                <b>ExportTables</b>
                {' '}
                permission.
              </>
            )
            : undefined
        )}
        disabled={!exportAllowed}
        color="primary"
        variant="outlined"
        {...tooltipButton}
        startIcon={children ? <GetApp /> : null}
        onClick={() => setOpenDialog(true)}
      >
        {children ? children : <GetApp />}
      </TooltipButton>
      <ExportDialog
        tableId={tableId}
        exportFields={exportFields}
        exportHandler={exportHandler}
        title={children as string ?? 'Export Table'}
        loading={exportLoading}
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      />
      <ErrorSnackbar {...exportErrorSnackbar}>
        Could not export table
      </ErrorSnackbar>
    </>
  );
}
